import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { Chatbot } from "./components/Chatbot";
import { CookieConsent } from "./components/CookieConsent";
import { EditModeProvider } from "./contexts/EditModeContext";
import { EditModeToggle } from "./components/EditModeToggle";
import { InlineFormPanel } from "./components/InlineFormPanel";
import LanguageWrapper from "./components/LanguageWrapper";
import "./i18n/config";
import { usePageTracking } from "./hooks/usePageTracking";

// Lazy load route components for better code splitting
const Home = lazy(() => import("./pages/Home"));
const Services = lazy(() => import("./pages/Services"));
const Methodology = lazy(() => import("./pages/Methodology"));
const CaseStudies = lazy(() => import("./pages/CaseStudies"));
const CaseStudyDetail = lazy(() => import("./pages/CaseStudyDetail"));
const About = lazy(() => import("./pages/About"));
const Blog = lazy(() => import("./pages/Blog"));
const Contact = lazy(() => import("./pages/Contact"));
const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
const Privacy = lazy(() => import("./pages/Privacy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const RootRedirect = () => {
  const savedLang = localStorage.getItem('language') || 'dk';
  return <Navigate to={`/${savedLang}`} replace />;
};

const TrackingWrapper = ({ children }: { children: React.ReactNode }) => {
  usePageTracking();
  return <>{children}</>;
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <TooltipProvider>
          <EditModeProvider>
            <Toaster />
            <Sonner />
            <Chatbot />
            <EditModeToggle />
            <InlineFormPanel />
            <BrowserRouter>
              <TrackingWrapper>
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>}>
                <Routes>
              {/* Redirect root to default language */}
              <Route path="/" element={<RootRedirect />} />
              
              {/* Language-specific routes */}
              <Route path="/:lang" element={<LanguageWrapper><Home /></LanguageWrapper>} />
              <Route path="/:lang/services" element={<LanguageWrapper><Services /></LanguageWrapper>} />
              <Route path="/:lang/methodology" element={<LanguageWrapper><Methodology /></LanguageWrapper>} />
              <Route path="/:lang/cases" element={<LanguageWrapper><CaseStudies /></LanguageWrapper>} />
              <Route path="/:lang/cases/:slug" element={<LanguageWrapper><CaseStudyDetail /></LanguageWrapper>} />
              <Route path="/:lang/about" element={<LanguageWrapper><About /></LanguageWrapper>} />
              <Route path="/:lang/blog" element={<LanguageWrapper><Blog /></LanguageWrapper>} />
              <Route path="/:lang/blog/:slug" element={<LanguageWrapper><Blog /></LanguageWrapper>} />
              <Route path="/:lang/contact" element={<LanguageWrapper><Contact /></LanguageWrapper>} />
              <Route path="/:lang/auth" element={<LanguageWrapper><Auth /></LanguageWrapper>} />
              <Route path="/:lang/admin" element={<LanguageWrapper><Admin /></LanguageWrapper>} />
              <Route path="/:lang/privacy" element={<LanguageWrapper><Privacy /></LanguageWrapper>} />
              <Route path="/:lang/cookies" element={<LanguageWrapper><CookiePolicy /></LanguageWrapper>} />
              
              {/* Catch-all */}
              <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              <CookieConsent />
              </TrackingWrapper>
            </BrowserRouter>
          </EditModeProvider>
        </TooltipProvider>
      </HelmetProvider>
    </QueryClientProvider>
  );
};

export default App;
